// src/components/SponsorForm/index.js
import React, { useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormLabel,
  IconButton,
  Image,
  Input,
  Select,
  Stack,
  useToast,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";
import { MdAddAPhoto } from "react-icons/md";
import client from "../../utils/client";
import { useNavigate } from "react-router-dom";
import { getImageUploadUrl } from "../../utils/s3";
import axios from "axios";

const SponsorForm = ({ sponsor }) => {
  const { register, handleSubmit } = useForm();
  const toast = useToast();
  const navigate = useNavigate();
  const fileInput = useRef(null);

  // Preview: existing S3 URL or local object URL
  const [previewImage, setPreviewImage] = useState(
    sponsor?.images?.[0]
      ? `https://${process.env.REACT_APP_S3_BUCKET}.s3.${process.env.REACT_APP_S3_REGION}.amazonaws.com/sponsors_images/${sponsor.images[0]}`
      : null
  );
  // Holds the File object selected by the user
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // When user picks a file: store it and set preview
  const handleImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Determine imageKey: existing or newly uploaded
      let imageKey = sponsor?.images?.[0];

      if (selectedFile) {
        const filename = `sponsorLogo-${v4()}.jpeg`;
        const key = `sponsors_images/${filename}`;

        // 1) get signed URL from back
        const signedUrl = await getImageUploadUrl(key, selectedFile.type);

        // 2) upload to S3
        await axios.put(signedUrl, selectedFile, {
          headers: { "Content-Type": selectedFile.type },
        });

        imageKey = filename;
      }

      // Build payload
      const payload = {
        images: [imageKey],
        name: data.name,
        targetUrl: data.targetUrl,
        type: data.type,
        isActive: data.isActive,
        endDate: data.endDate,
      };

      // Create or update
      if (sponsor) {
        await client.patch(`/sponsors/${sponsor.sponsorId}`, payload);
        toast({
          title: "Patrocinador alterado com sucesso",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      } else {
        await client.post("/sponsors", payload);
        toast({
          title: "Patrocinador cadastrado com sucesso",
          description: "O patrocinador foi cadastrado!",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      }

      navigate("/allSponsors");
    } catch (error) {
      console.error(error);
      toast({
        title: "Algum erro aconteceu",
        description: "Contate o Administrador do Sistema",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex flexDir="column" gap="5" my={5}>
        <Box border="1px solid #ccc" borderRadius="8px" p={[2, 5]} bg="white">
          <Badge fontSize="1rem" colorScheme="green" mb={2}>
            Informações do Patrocinador
          </Badge>
          <Flex flexDir="column" gap={3}>
            <Stack direction={["column", "row"]}>
              <FormLabel w="100%" m={0}>
                Nome do Patrocinador
                <Input
                  required
                  type="text"
                  placeholder="Nome do Patrocinador"
                  {...register("name")}
                  defaultValue={sponsor?.name}
                />
              </FormLabel>
            </Stack>
            <Stack direction={["column", "row"]}>
              <FormLabel w="100%" m={0}>
                Link de destino
                <Input
                  required
                  type="text"
                  placeholder="http://..."
                  {...register("targetUrl")}
                  defaultValue={sponsor?.targetUrl}
                />
              </FormLabel>
            </Stack>
            <Stack direction={["column", "row"]}>
              <FormLabel w="100%" m={0}>
                Tipo de Patrocinador
                <Select
                  required
                  {...register("type")}
                  defaultValue={sponsor?.type || ""}
                >
                  <option value="" disabled>
                    Selecione o tipo
                  </option>
                  <option value="Principal">Principal</option>
                  <option value="Secundário">Secundário</option>
                </Select>
              </FormLabel>
              <FormLabel w="100%" m={0}>
                Status
                <Select
                  required
                  {...register("isActive")}
                  defaultValue={sponsor?.isActive || "Ativo"}
                >
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                </Select>
              </FormLabel>
              <FormLabel w="100%" m={0}>
                Término do Patrocínio
                <Input
                  type="date"
                  {...register("endDate")}
                  defaultValue={sponsor?.endDate}
                />
              </FormLabel>
            </Stack>
          </Flex>
        </Box>

        <Box p={[2, 5]} bg="white" border="1px solid #ccc" borderRadius="8px">
          <Badge fontSize="1rem" colorScheme="green" mb={2}>
            Imagem do patrocinador para ser veiculada no Site
          </Badge>
          <Flex
            flexDir={["column", "row"]}
            alignItems="center"
            justifyContent="space-between"
            gap={5}
          >
            <div>
              <Input
                type="file"
                accept="image/*"
                id="fileInput"
                display="none"
                onChange={handleImage}
                ref={fileInput}
              />
              <label htmlFor="fileInput">
                <Flex alignItems="center" gap={10}>
                  <FormLabel m={0}>Adicione a imagem do Patrocinador</FormLabel>
                  <IconButton
                    colorScheme="green"
                    borderRadius="100%"
                    w={["70px", "85px"]}
                    h={["70px", "85px"]}
                    aria-label="Upload File"
                    icon={<MdAddAPhoto fontSize="45px" />}
                    onClick={() => fileInput.current.click()}
                  />
                </Flex>
              </label>
            </div>
            <Flex justifyContent="center" w="100%">
              {previewImage && <Image objectFit="cover" src={previewImage} />}
            </Flex>
          </Flex>
        </Box>

        <Flex justifyContent="center">
          <Button
            isLoading={isLoading}
            colorScheme="green"
            w={["100%", "40%"]}
            type="submit"
          >
            {sponsor ? "Editar Patrocinador" : "Cadastrar Patrocinador"}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default SponsorForm;
